import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';

@Component({
  selector: 'app-dashboard-approval-bonds',
  templateUrl: './dashboard-approval-bonds.component.html',
  styleUrls: ['./dashboard-approval-bonds.component.scss'],
})
export class DashboardApprovalBondsComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() bonds: BondValues[] = [];
  @Input() comparingVersions: boolean = false;
  isShareholder = false;

  // Dados para versão principal
  mainBonds: BondValues[] = [];
  mainLoading = false;

  // Dados para versão comparando
  comparingBonds: BondValues[] = [];
  comparingLoading = false;
  comparingDataLoading = false;

  // Loading para modo normal
  loading = false;

  eventMainVersion: any;
  eventComparingVersion: any;
  eventVersion: any;

  constructor(
    private api: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isShareholder = this.router.url.includes('shareholder');

    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      // Buscar dados da API v3 se não houver no cache
      if (!this.bonds || this.bonds.length === 0) {
        this.loading = true;
        this.getBondsData();
      }
    }

    // Listener para mudanças de versão no modo "visualizar todas"
    this.eventVersion = this.approvalService.changeVersion.subscribe(() => {
      if (!this.comparingVersions) {
        // Incrementar contador de loading v3 quando iniciar requisição
        this.approvalService.incrementV3Loading();

        // Ativar loading em todas as abas quando trocar de versão
        this.loading = true;
        this.getBondsData();
      }
    });

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
    if (this.eventVersion) {
      this.eventVersion.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Detectar quando comparingVersions muda de true para false
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange &&
      changes['comparingVersions'].previousValue === true &&
      changes['comparingVersions'].currentValue === false
    ) {
      // Recarregar dados para visualização normal
      if (!this.bonds || this.bonds.length === 0) {
        this.getBondsData();
      }
    }
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainBonds = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.cdr.detectChanges();
    this.getBondsData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingBonds = [];
      this.comparingDataLoading = false;
      this.comparingLoading = false;
      return;
    }
    this.comparingDataLoading = true;
    this.comparingLoading = true;
    this.cdr.detectChanges();
    this.getBondsData('comparing');
  }

  private canRunComparison(): boolean {
    return (
      this.comparingVersions &&
      !this.mainLoading &&
      !this.comparingDataLoading &&
      !!this.mainBonds &&
      !!this.comparingBonds
    );
  }

  // Função para comparar os dados e aplicar classes de comparação
  async compareData() {
    try {
      if (!this.canRunComparison()) {
        return;
      }

      if (!this.mainBonds || !this.comparingBonds) {
        return;
      }

      const processedData: any[] = [];

      // Processar itens da tabela de comparação (direita)
      this.comparingBonds.forEach((comparingItem: any) => {
        // Comparar pelo fund_id, não pelo id do assignor_fund_interest
        const referenceItem = this.mainBonds.find(
          (ref: any) => ref.fund_id === comparingItem.fund_id
        );

        if (referenceItem) {
          // Item existe em ambas as tabelas - verificar se há diferenças
          const changedFields: any = {};
          let hasChanges = false;

          // Comparar campos específicos
          if (referenceItem.name !== comparingItem.name) {
            changedFields.name = true;
            hasChanges = true;
          }
          if (referenceItem.short_name !== comparingItem.short_name) {
            changedFields.short_name = true;
            hasChanges = true;
          }
          if (referenceItem.document !== comparingItem.document) {
            changedFields.document = true;
            hasChanges = true;
          }
          if (
            referenceItem.anbima_classification_label !==
            comparingItem.anbima_classification_label
          ) {
            changedFields.anbima_classification_label = true;
            hasChanges = true;
          }
          if (referenceItem.coobligation !== comparingItem.coobligation) {
            changedFields.coobligation = true;
            hasChanges = true;
          }

          processedData.push({
            ...comparingItem,
            compareClass: hasChanges ? 'row-compare-modified' : '',
            changedFields: hasChanges ? changedFields : {},
          });
        } else {
          // Item novo (existe só na direita)
          processedData.push({
            ...comparingItem,
            compareClass: 'row-compare-added',
            changedFields: {},
          });
        }
      });

      // Adicionar itens removidos (existem só na esquerda)
      this.mainBonds.forEach((referenceItem: any) => {
        // Comparar pelo fund_id, não pelo id do assignor_fund_interest
        const existsInComparing = this.comparingBonds.find(
          (comp: any) => comp.fund_id === referenceItem.fund_id
        );

        if (!existsInComparing) {
          // Item removido - adicionar na lista de comparação com classe removed
          processedData.push({
            ...referenceItem,
            compareClass: 'row-compare-removed',
            changedFields: {},
          });
        }
      });

      this.comparingBonds = processedData;
      this.cdr.detectChanges();
    } catch (error) {
    }
  }

  async getBondsData(versionType: 'main' | 'comparing' | null = null) {
    try {
      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        throw new Error('Register UUID não encontrado na rota');
      }

      const params: any = {
        data_type: 'assignor_fund_interest',
      };

      // Determinar qual versão buscar
      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        params.version = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        params.version = this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        params.version = this.approvalService.approvalData.version;
      } else {
        params.last = true;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainVersion) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingVersion))
      ) {
        if (versionType === 'main') {
          this.mainBonds = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingBonds = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          this.cdr.detectChanges();
        }
        return;
      }

      const response = await this.api.get<any>({
        route: `api/v3/registration/versions/register/${register_uuid}/`,
        params: params,
        token: true,
      });

      if (
        !response ||
        !response.data ||
        !response.data.versions?.assignor_fund_interest
      ) {
        throw new Error('Resposta da API inválida');
      }

      // Extrair dados da versão
      const versions =
        response.data.versions.assignor_fund_interest.versions || [];

      if (versions.length === 0) {
        if (versionType === 'main') {
          this.mainBonds = [];
          this.mainLoading = false;
          if (this.canRunComparison()) {
            await this.compareData();
          }
          this.cdr.detectChanges();
          return;
        } else if (versionType === 'comparing') {
          this.comparingBonds = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          if (this.canRunComparison()) {
            await this.compareData();
          }
          this.cdr.detectChanges();
          return;
        } else {
          this.bonds = [];
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }
      }

      const versionData = versions[0];
      const fundsData = versionData?.data || [];

      // Processar dados diretamente da API v3
      // O payload pode retornar fund como ID numérico ou objeto expandido
      // Estrutura: response.data.versions.assignor_fund_interest.versions[0].data[]
      // Cada item tem: { id, fund (ID ou objeto), register, co_obligation }
      const processedData: (BondValues & { fund_id?: number })[] =
        fundsData.map((item: any) => {
          // Verificar se fund é um objeto expandido ou apenas um ID
          const fundId =
            typeof item.fund === 'object' ? item.fund?.id : item.fund;
          const fund = typeof item.fund === 'object' ? item.fund : null;

          return {
            id: item.id, // ID do assignor_fund_interest
            fund_id: fundId, // ID do fundo
            name: fund?.name || item.name || '',
            short_name:
              fund?.fantasy_name || fund?.short_name || item.short_name || '',
            document:
              fund?.document || fund?.document_number || item.document || '',
            anbima_classification_label:
              fund?.anbima_classification ||
              fund?.anbima_classification_label ||
              fund?.anbima_classification_display ||
              item.anbima_classification_label ||
              '',
            // co_obligation está no item, não no fundo
            coobligation:
              item.co_obligation !== undefined
                ? item.co_obligation
                : item.coobligation !== undefined
                ? item.coobligation
                : false,
            status: fund?.status || item.status || '',
          };
        });

      if (versionType === 'main') {
        this.mainBonds = processedData as BondValues[];
        this.mainLoading = false;
        // Se já temos comparing data, fazer comparação
        if (this.canRunComparison()) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingBonds = processedData as BondValues[];
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        // Se já temos main data, fazer comparação
        if (this.canRunComparison()) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else {
        this.bonds = processedData as BondValues[];
        this.loading = false;
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Erro ao buscar dados de fundos vinculados:', error);
      if (versionType === 'main') {
        this.mainBonds = [];
        this.mainLoading = false;
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingBonds = [];
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        this.cdr.detectChanges();
      } else {
        this.bonds = [];
        this.loading = false;
        this.cdr.detectChanges();
      }
    } finally {
      // Decrementar contador de loading v3 quando terminar requisição (apenas no modo normal, não comparação)
      if (!versionType && !this.comparingVersions) {
        this.approvalService.decrementV3Loading();
      }
    }

    /* Código antigo comentado - não chamar APIs v2
    try {
      const req_params: any = {
        register: this.route.snapshot.params.id,
        tab: 'funds',
      };

      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        req_params['version'] = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        req_params['version'] =
          this.approvalService.approvalData.comparingVersion;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainVersion) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingVersion))
      ) {
        if (versionType === 'main') {
          this.mainBonds = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingBonds = [];
          this.comparingLoading = false;
          this.cdr.detectChanges();
        }
        return;
      }

      // Se estiver em modo de comparação mas não tiver parâmetros de versão, não fazer requisição
      if (this.comparingVersions && versionType && !req_params['version']) {
        if (versionType === 'main') {
          this.mainBonds = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingBonds = [];
          this.comparingLoading = false;
          this.cdr.detectChanges();
        }
        return;
      }

      /* Código antigo comentado - não chamar APIs v2
      const data = await this.api.get<any[]>({
        route: `api/v2/approvals/version/`,
        token: true,
        params: req_params,
      });

      // Processar os dados da mesma forma que getFundsLinks
      const fundsData = data[0]?.register?.funds || [];
      const processedData = fundsData.map((item: any) => {
        const fund = item.fund || {};
        return {
          short_name: fund.fantasy_name || fund.short_name || '',
          name: fund.name || '',
          anbima_classification_label:
            fund.anbima_classification ||
            fund.anbima_classification_label ||
            '',
          id: item.id || fund.id,
          document: fund.document || '',
          coobligation: item.co_obligation || item.coobligation || false,
        };
      });

      if (versionType === 'main') {
        this.mainBonds = processedData;
        this.mainLoading = false;
        // Se já temos comparing data, fazer comparação
        if (this.comparingVersions && this.comparingBonds) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingBonds = processedData;
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        // Se já temos main data, fazer comparação
        if (this.comparingVersions && this.mainBonds) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else {
        // Modo normal - processar dados da mesma forma
        const fundsData = data[0]?.register?.funds || [];
        const processedData = fundsData.map((item: any) => {
          const fund = item.fund || {};
          return {
            short_name: fund.fantasy_name || fund.short_name || '',
            name: fund.name || '',
            anbima_classification_label:
              fund.anbima_classification ||
              fund.anbima_classification_label ||
              '',
            id: item.id || fund.id,
            document: fund.document || '',
            coobligation: item.co_obligation || item.coobligation || false,
          };
        });
        this.bonds = processedData;
        this.loading = false;
      }
    } catch (error) {
      if (versionType === 'main') {
        this.mainLoading = false;
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        this.cdr.detectChanges();
      }
    }
    */
  }

  // Funções auxiliares para verificar campos alterados
  isFieldChanged(item: any, field: string): boolean {
    if (!item || !item.changedFields) return false;
    return item.changedFields[field] === true;
  }

  getFundInitials(fund: any) {
    if (!fund) {
      return '';
    }

    return fund.name
      ? fund.name
          .toUpperCase()
          .replace(/[^\w\s]/gi, '')
          .split(' ')
          .slice(0, 2)
          .map((word: string) => word.charAt(0))
          .join('')
      : '';
  }

  formatCnpj(value: string): string {
    if (!value) return '';
    return Utils.formatDocumentForDisplay(value);
  }
}
